import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Device, PairedRuuviTag, RuuviTag } from '../../types'
import { getPairedRuuviTags, invokeAction } from '../../services/deviceService'
import { Alert, AlertTitle, Box, Button, Stack, Grid } from '@mui/material'
import { BluetoothSearching } from '@mui/icons-material'
import LoadingState from '../LoadingState'
import RuuviTagMacAddressInput from './RuuviTagMacAddressInput'
import RuuviTagScannerList from './RuuviTagScannerList'
import ValveRuuviTagInstallationInstructionsAlert from '../ValveRuuviTagInstallationInstructionsAlert'
import { DeviceType } from '../../enums'
import {
  createOrUpdateRuuviTag,
  getRuuviTagAccelerationNow,
  getRuuviTagConditionNow
} from '../../services/ruuviTagService'
import { useDispatch } from 'react-redux'
import {
  createErrorNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'
import { LoadingButton } from '@mui/lab'
import RuuviTagInstallationInfoAlert from '../RuuviTagInstallationInfoAlert'

export interface RuuviTagScannerProps {
  device: Device
  onChange: (ruuviTag: RuuviTag) => void
  onCancel: () => void
  onFound?: (ruuviTag: RuuviTag) => void
  afterLoad?: (ruuviTag: RuuviTag) => void
  afterCreate?: (ruuviTag: RuuviTag) => void
}

function RuuviTagScanner({
  device,
  onFound,
  onChange,
  onCancel,
  afterLoad: _afterLoad,
  afterCreate
}: RuuviTagScannerProps) {
  const { t } = useTranslation('installation')

  const dispatch = useDispatch()

  const abortControllerRef = useRef<AbortController | null>(null)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [isScanning, setScanning] = useState<boolean>(false)

  const [isScanCompleted, setScanCompleted] = useState<boolean>(false)

  const [isLoading] = useState<boolean>(false)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [showCreateRuuviTagAlert, setCreateRuuviTagAlert] = useState<boolean>(false)

  const [foundRuuviTags, setFoundRuuviTags] = useState<PairedRuuviTag[]>([])

  const [macAddress, setMacAddress] = useState<string>('')
  const [isRefreshingOrientation, setRefreshingOrientation] = useState<boolean>(false)
  // We rely on paired tag response to include latest readings; no extra condition fetch.
  // const [fetchingTemp] = useState<Record<string, boolean>>({})

  // When scanning flag turns on, start the scan loop; clean up on stop/unmount
  useEffect(() => {
    if (!isScanning) return
    const controller = new AbortController()
    abortControllerRef.current = controller
    setScanCompleted(false)
    scanRuuviTags(controller).catch(() => {})
    return () => {
      try {
        controller.abort()
      } catch {
        /* ignore fetch errors within polling loop */
      }
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isScanning])

  async function scanRuuviTags(controller?: AbortController) {
    if (!isScanning) {
      setScanning(true)
    }

    setFoundRuuviTags([])

    // Invoke action for the device.
    await invokeAction(
      device.name,
      {
        ruuvi_pairing: 60
      },
      {
        signal: controller?.signal
      }
    )

    // Poll for the paired ruuvi tags every 2 seconds.
    const TWO_SECONDS = 2 * 1000
    const interval = setInterval(async () => {
      try {
        const tags = await getPairedRuuviTags(device.name, { signal: controller?.signal })
        if (Array.isArray(tags)) {
          setFoundRuuviTags((prev) => {
            const prevIds = new Set(prev.map((t) => t.id))
            const newOnes = tags.filter((t) => !prevIds.has(t.id))
            newOnes.forEach((t) => onFound?.(t))
            // Preserve previous list if API returns empty to avoid flicker
            return tags.length > 0 ? (tags as PairedRuuviTag[]) : prev
          })
        }
      } catch (_) {
        // Do nothing if failed.
      }
    }, TWO_SECONDS)
    intervalRef.current = interval

    // Clear the interval after one minute.
    const ONE_MINUTE = 60 * 1000
    const timeout = setTimeout(() => {
      setScanning(false)
      setScanCompleted(true)
      clearInterval(interval)
    }, ONE_MINUTE)
    timeoutRef.current = timeout
  }
  // No extra fetchConditionsForTag; sensor-backend should provide latest condition when pairing.

  function startScan() {
    if (isScanning) return
    setScanCompleted(false)
    setScanning(true)
  }

  function handleScanAgain() {
    startScan()
  }

  async function handleRefreshOrientation() {
    if (isRefreshingOrientation || isScanning || foundRuuviTags.length === 0) {
      return
    }

    setRefreshingOrientation(true)
    try {
      const updatedTags = await Promise.all(
        foundRuuviTags.map(async (tag) => {
          const [conditionResult, accelerationResult] = await Promise.allSettled([
            getRuuviTagConditionNow(tag.id),
            getRuuviTagAccelerationNow(tag.id)
          ])

          const updatedTag: PairedRuuviTag = {
            ...tag,
            latest: {
              ...tag.latest,
              condition:
                conditionResult.status === 'fulfilled'
                  ? conditionResult.value
                  : tag.latest?.condition,
              acceleration:
                accelerationResult.status === 'fulfilled'
                  ? accelerationResult.value
                  : tag.latest?.acceleration
            }
          }

          return updatedTag
        })
      )

      setFoundRuuviTags(updatedTags)
    } catch (_error) {
      dispatch(createErrorNotification(t('somethingWentWrong')))
    } finally {
      setRefreshingOrientation(false)
    }
  }

  function handleStartScan() {
    startScan()
  }

  function handleCancelScan() {
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort()
      } catch {
        /* ignore abort errors */
      }
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setScanning(false)
    setScanCompleted(false)
  }

  async function handleSubmit() {
    try {
      setSubmitting(true)
      const values = { id: macAddress, device: device?.name ?? null, asset: undefined }
      const ruuviTag = await createOrUpdateRuuviTag(values)
      setSubmitting(false)
      setCreateRuuviTagAlert(false)
      dispatch(createSuccessNotification(t('ruuviTagWasCreatedSuccessfully')))
      afterCreate?.(ruuviTag)
      onChange(ruuviTag)
    } catch (_error) {
      setSubmitting(false)
      dispatch(createErrorNotification(t('somethingWentWrong')))
    }
  }

  return (
    <>
      <Stack direction="column" gap={2}>
        <RuuviTagInstallationInfoAlert />
        {device?.type === DeviceType.VALVE && (
          <ValveRuuviTagInstallationInstructionsAlert
            valve={device as unknown as import('../../types').Valve}
          />
        )}

        {isScanning ? (
          <>
            <LoadingState
              title={
                <Stack direction="row" gap={1} alignItems="center">
                  <BluetoothSearching fontSize="small" />
                  <span>{t('searchingRuuviTags')}</span>
                </Stack>
              }
              action={
                <Button variant="outlined" color="inherit" onClick={handleCancelScan}>
                  {t('cancel')}
                </Button>
              }
            />

            {foundRuuviTags.length > 0 && (
              <RuuviTagScannerList
                ruuviTags={foundRuuviTags}
                onSelect={onChange}
                onExitInstallation={onCancel}
              />
            )}
          </>
        ) : isScanCompleted ? (
          <>
            {foundRuuviTags.length === 0 ? (
              <Alert severity="warning">
                <AlertTitle sx={{ m: 0 }}>{t('ruuviTagsWereNotFound')}</AlertTitle>
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
                    {t('cancel')}
                  </Button>
                </Stack>
              </Alert>
            ) : (
              <Alert severity="info">
                <AlertTitle sx={{ m: 0 }}>{t('scanCompleted')}</AlertTitle>
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <LoadingButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    loading={isRefreshingOrientation}
                    disabled={isRefreshingOrientation}
                    onClick={handleRefreshOrientation}
                  >
                    {t('refreshOrientation')}
                  </LoadingButton>
                  <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
                    {t('cancel')}
                  </Button>
                </Stack>
              </Alert>
            )}

            {foundRuuviTags.length > 0 && (
              <RuuviTagScannerList
                ruuviTags={foundRuuviTags}
                onSelect={onChange}
                onExitInstallation={onCancel}
              />
            )}
          </>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1 }}
                  onClick={handleStartScan}
                >
                  {t('startScan')}
                </Button>
              </Grid>
            </Grid>

            {foundRuuviTags.length > 0 && (
              <>
                <RuuviTagScannerList
                  ruuviTags={foundRuuviTags}
                  onSelect={onChange}
                  onExitInstallation={onCancel}
                />
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <LoadingButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    loading={isRefreshingOrientation}
                    onClick={handleRefreshOrientation}
                    disabled={isRefreshingOrientation}
                  >
                    {t('refreshOrientation')}
                  </LoadingButton>
                </Stack>
              </>
            )}
          </>
        )}

        {/* Manual MAC input and create prompt */}
        <Box sx={{ mt: 2 }}>
          <RuuviTagMacAddressInput disabled={isLoading || isSubmitting} onChange={setMacAddress} />

          {showCreateRuuviTagAlert && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle sx={{ m: 0 }}>{t('doYouWantToCreateNewRuuviTag')}</AlertTitle>
              <Stack direction="row" gap={1} sx={{ mt: 2 }}>
                <LoadingButton
                  variant="outlined"
                  color="inherit"
                  size="small"
                  loading={isSubmitting}
                  onClick={handleSubmit}
                >
                  {t('yes')}
                </LoadingButton>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  disabled={isSubmitting}
                  onClick={() => setCreateRuuviTagAlert(false)}
                >
                  {t('no')}
                </Button>
              </Stack>
            </Alert>
          )}
        </Box>
      </Stack>
    </>
  )
}

export default RuuviTagScanner
